import React, { useState, useMemo } from 'react';
import { Clock, ChevronDown } from 'lucide-react';

interface CronPickerProps {
  value: string;
  onChange: (cron: string) => void;
}

const PRESETS: { label: string; cron: string }[] = [
  { label: 'Every 15 min', cron: '*/15 * * * *' },
  { label: 'Every 30 min', cron: '0,30 * * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Daily midnight', cron: '0 0 * * *' },
  { label: 'Daily 6 AM', cron: '0 6 * * *' },
  { label: 'Weekdays 6 AM', cron: '0 6 * * 1-5' },
  { label: 'Monday 6 AM', cron: '0 6 * * 1' },
];

const MINUTE_OPTIONS = [
  { label: 'Every minute', value: '*' },
  { label: 'Every 5 min', value: '*/5' },
  { label: 'Every 10 min', value: '*/10' },
  { label: 'Every 15 min', value: '*/15' },
  { label: 'Every 30 min', value: '*/30' },
  ...Array.from({ length: 60 }, (_, i) => ({ label: `:${String(i).padStart(2, '0')}`, value: String(i) })),
];

const HOUR_OPTIONS = [
  { label: 'Every hour', value: '*' },
  { label: 'Every 2 hours', value: '*/2' },
  { label: 'Every 4 hours', value: '*/4' },
  { label: 'Every 6 hours', value: '*/6' },
  { label: 'Every 12 hours', value: '*/12' },
  ...Array.from({ length: 24 }, (_, i) => ({ label: `${i}:00 (${formatHour(i)})`, value: String(i) })),
];

const DAY_OF_MONTH_OPTIONS = [
  { label: 'Every day', value: '*' },
  ...Array.from({ length: 31 }, (_, i) => ({ label: String(i + 1), value: String(i + 1) })),
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_OPTIONS = [
  { label: 'Every month', value: '*' },
  ...MONTH_NAMES.map((name, i) => ({ label: name, value: String(i + 1) })),
];

const DOW_OPTIONS = [
  { label: 'Every day', value: '*' },
  { label: 'Weekdays', value: '1-5' },
  { label: 'Weekends', value: '0,6' },
  { label: 'Sun', value: '0' },
  { label: 'Mon', value: '1' },
  { label: 'Tue', value: '2' },
  { label: 'Wed', value: '3' },
  { label: 'Thu', value: '4' },
  { label: 'Fri', value: '5' },
  { label: 'Sat', value: '6' },
];

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function formatHourMinute(h: number, m: number): string {
  const period = h < 12 ? 'AM' : 'PM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function describeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, month, dow] = parts;

  // Every N minutes
  if (min.startsWith('*/') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return `Every ${min.slice(2)} minutes`;
  }
  // Specific minutes each hour (e.g. 0,30)
  if (min.includes(',') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return `At minute ${min} every hour`;
  }
  // Every hour at :MM
  if (/^\d+$/.test(min) && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    return min === '0' ? 'Every hour' : `Every hour at :${min.padStart(2, '0')}`;
  }
  // Every N hours
  if (/^\d+$/.test(min) && hour.startsWith('*/') && dom === '*' && month === '*' && dow === '*') {
    return `Every ${hour.slice(2)} hours`;
  }

  // Specific hour and minute
  if (/^\d+$/.test(min) && /^\d+$/.test(hour)) {
    const h = parseInt(hour, 10);
    const m = parseInt(min, 10);
    const time = formatHourMinute(h, m);

    if (dom === '*' && month === '*' && dow === '*') return `Every day at ${time}`;
    if (dom === '*' && month === '*' && dow === '1-5') return `Weekdays at ${time}`;
    if (dom === '*' && month === '*' && dow === '0,6') return `Weekends at ${time}`;
    if (dom === '*' && month === '*' && /^\d$/.test(dow)) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `${days[parseInt(dow, 10)] ?? dow} at ${time}`;
    }
    if (/^\d+$/.test(dom) && month === '*' && dow === '*') {
      return `Monthly on the ${ordinal(parseInt(dom, 10))} at ${time}`;
    }
  }

  return cron;
}

export function getNextRuns(cron: string, count: number): Date[] {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return [];
  const [minF, hourF, domF, monthF, dowF] = parts;

  const results: Date[] = [];
  const now = new Date();
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes() + 1, 0, 0);

  const maxIter = 525960; // ~1 year of minutes
  for (let i = 0; i < maxIter && results.length < count; i++) {
    if (
      matchesCronField(cursor.getMonth() + 1, monthF) &&
      matchesCronField(cursor.getDate(), domF) &&
      matchesCronField(cursor.getDay(), dowF) &&
      matchesCronField(cursor.getHours(), hourF) &&
      matchesCronField(cursor.getMinutes(), minF)
    ) {
      results.push(new Date(cursor));
    }
    cursor.setMinutes(cursor.getMinutes() + 1);
  }
  return results;
}

function matchesCronField(value: number, field: string): boolean {
  if (field === '*') return true;
  // Step: */N
  if (field.startsWith('*/')) {
    const step = parseInt(field.slice(2), 10);
    return step > 0 && value % step === 0;
  }
  // Range: A-B
  if (field.includes('-') && !field.includes(',')) {
    const [a, b] = field.split('-').map(Number);
    return value >= a && value <= b;
  }
  // List: A,B,C
  if (field.includes(',')) {
    return field.split(',').some((p) => matchesCronField(value, p.trim()));
  }
  // Exact
  return parseInt(field, 10) === value;
}

function parseCronParts(cron: string): [string, string, string, string, string] {
  const parts = cron.trim().split(/\s+/);
  return [parts[0] ?? '*', parts[1] ?? '*', parts[2] ?? '*', parts[3] ?? '*', parts[4] ?? '*'];
}

const selectClass =
  'w-full px-2 py-1.5 border border-strong rounded-sm bg-surface text-heading text-sm appearance-none';

export const CronPicker: React.FC<CronPickerProps> = ({ value, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const isPreset = PRESETS.some((p) => p.cron === value);

  const [min, hour, dom, month, dow] = parseCronParts(value);
  const description = useMemo(() => describeCron(value), [value]);
  const nextRuns = useMemo(() => getNextRuns(value, 3), [value]);

  const updateField = (index: number, val: string) => {
    const parts = parseCronParts(value);
    parts[index] = val;
    onChange(parts.join(' '));
  };

  return (
    <div className="space-y-2">
      {/* Preset pills */}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.cron}
            type="button"
            onClick={() => {
              onChange(p.cron);
              setShowCustom(false);
            }}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
              value === p.cron
                ? 'bg-accent text-white border-transparent'
                : 'bg-surface-alt text-muted border hover:text-heading hover:border-strong'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowCustom(!showCustom)}
          className={`px-2.5 py-1 text-xs rounded-full border transition-colors flex items-center gap-1 ${
            !isPreset && !showCustom
              ? 'bg-accent text-white border-transparent'
              : showCustom
                ? 'bg-accent text-white border-transparent'
                : 'bg-surface-alt text-muted border hover:text-heading hover:border-strong'
          }`}
        >
          Custom{' '}
          <ChevronDown size={10} className={showCustom ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
      </div>

      {/* Custom dropdowns */}
      {showCustom && (
        <div className="grid grid-cols-5 gap-2">
          <div>
            <label className="block text-[10px] text-faint mb-0.5">Minute</label>
            <select value={min} onChange={(e) => updateField(0, e.target.value)} className={selectClass}>
              {MINUTE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-faint mb-0.5">Hour</label>
            <select value={hour} onChange={(e) => updateField(1, e.target.value)} className={selectClass}>
              {HOUR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-faint mb-0.5">Day</label>
            <select value={dom} onChange={(e) => updateField(2, e.target.value)} className={selectClass}>
              {DAY_OF_MONTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-faint mb-0.5">Month</label>
            <select value={month} onChange={(e) => updateField(3, e.target.value)} className={selectClass}>
              {MONTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-faint mb-0.5">Weekday</label>
            <select value={dow} onChange={(e) => updateField(4, e.target.value)} className={selectClass}>
              {DOW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-faint shrink-0" />
          <span className="text-sm text-muted italic">{description}</span>
        </div>
        {nextRuns.length > 0 && (
          <div className="text-right shrink-0">
            <span className="text-[10px] text-faint block mb-0.5">Next runs</span>
            {nextRuns.map((d, i) => (
              <div key={i} className="text-xs text-faint leading-tight">
                {d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
